// Skirmish setup screen (GDD §10.1, §11): the game mode, the opponent — the computer (the enemy nation, the AI
// difficulty and an optional seed) or online (a rival from your continent, or a friend you challenge;
// docs/ARCHITECTURE.md §15) — and your nation. A DOM overlay over the (idle) canvas. Each mode card states the mode's
// rules (modes.ts, read from content); the setup sent to the local room lists the belligerents in seat order.

import './setup.css'
import { AI_TIERS, AI_TIER_DEFS, COUNTRIES, GAME_MODE_KEYS, type AiTier, type CountryCode, type GameModeKey } from '@taxwar/content'
import type { Belligerent, SeatControl } from '@taxwar/net'
import { isModePlayable } from '../../app/release'
import type { Screen, ScreenContext } from '../../app/screen'
import type { InviteChallenge, SetupActions } from '../../match/contracts'
import { newChallenge, playableOnline, type OnlineEntry } from '../../online'
import type { VisualSettings } from '../../render/visualSettings'
import { AI_TIER_PRESENTATION } from '../../hud/catalog'
import { button, canvasHost, el, TextSlot } from '../../hud/dom'
import { HudError } from '../../hud/errors'
import { applyScreenTheme } from '../../hud/theme'
import { CountryPicker } from './countryPicker'
import { MODE_PRESENTATION, modeRules } from './modes'
import { parseSeed, randomSeed, RANDOM_SEED, SEED_MAX, type SeedEntry } from './seed'
import type { TuningLink } from '../../tuning/link'

/** SkirmishSetup.belligerents is in seat order: the human plays this seat, the AI enemy the other one. */
const HUMAN_SEAT = 0
const HUMAN_CONTROL: SeatControl = { kind: 'human' }
const DEFAULT_MODE: GameModeKey = 'duel'
const DEFAULT_AI_TIER: AiTier = 'veteran'
const SEED_ERROR = `Seeds are whole numbers from 0 to ${SEED_MAX}.`
const ONLINE_ONLY_DUELS = 'Online wars are Country vs Country Duels for now.'

type Opponent = 'computer' | 'online'
const SEED_HELP = 'Leave blank for a random map. Enter a seed to replay one.'

function countryName(code: CountryCode): string {
  const country = COUNTRIES.get(code)
  if (country === undefined) throw new HudError(`country ${code} is not in the content dataset`)
  return country.name
}

/**
 * One mode option: the whole card is the radio's label, so a click anywhere picks it. The radio is named by the mode
 * name alone and described by the tagline and the rules, so a screen reader does not read the rules as its name.
 */
function modeCard(mode: GameModeKey, onSelect: (mode: GameModeKey) => void, signal: AbortSignal): HTMLLabelElement {
  const presentation = MODE_PRESENTATION[mode]
  const nameId = `tw-setup-mode-${mode}-name`
  const taglineId = `tw-setup-mode-${mode}-tagline`
  const rulesId = `tw-setup-mode-${mode}-rules`
  const input = el('input', 'tw-setup__mode-radio')
  input.type = 'radio'
  input.name = 'tw-setup-mode'
  input.value = mode
  input.checked = mode === DEFAULT_MODE
  input.setAttribute('aria-labelledby', nameId)
  input.setAttribute('aria-describedby', `${taglineId} ${rulesId}`)
  input.addEventListener('change', () => onSelect(mode), { signal })
  const name = el('span', 'tw-setup__mode-name', presentation.name)
  name.id = nameId
  const tagline = el('span', 'tw-setup__mode-tagline tw-dim', presentation.tagline)
  tagline.id = taglineId
  // A label holds phrasing content only, so the rules are spans carrying list semantics.
  const rules = el('span', 'tw-setup__mode-rules')
  rules.id = rulesId
  rules.setAttribute('role', 'list')
  for (const line of modeRules(mode)) {
    const rule = el('span', 'tw-setup__mode-rule', line)
    rule.setAttribute('role', 'listitem')
    rules.append(rule)
  }
  const card = el('label', 'tw-setup__mode')
  card.append(input, name, tagline, rules)
  return card
}

/**
 * A mode this build does not offer yet (app/release.ts): the same card, greyed behind a COMING SOON stamp with its
 * teaser, and nothing to select. Its rules stay visible but blurred — the tease is what is coming.
 */
function comingSoonCard(mode: GameModeKey): HTMLElement {
  const presentation = MODE_PRESENTATION[mode]
  const card = el('div', 'tw-setup__mode tw-setup__mode--coming-soon')
  card.setAttribute('aria-disabled', 'true')
  const stamp = el('span', 'tw-setup__mode-stamp')
  stamp.append(el('span', 'tw-setup__mode-stamp-title', 'Coming soon'), el('span', 'tw-setup__mode-stamp-teaser', presentation.teaser))
  const rules = el('span', 'tw-setup__mode-rules')
  rules.setAttribute('aria-hidden', 'true')
  for (const line of modeRules(mode)) rules.append(el('span', 'tw-setup__mode-rule', line))
  card.append(el('span', 'tw-setup__mode-name', presentation.name), el('span', 'tw-setup__mode-tagline tw-dim', presentation.tagline), rules, stamp)
  return card
}

export class SetupScreen implements Screen {
  private readonly overlay: HTMLElement
  private readonly tuning: TuningLink
  private readonly you: CountryPicker
  private readonly enemy: CountryPicker
  private readonly seedInput: HTMLInputElement
  private readonly seedNote: TextSlot
  private readonly summary: TextSlot
  private readonly startButton: HTMLButtonElement
  private readonly onlineInput: HTMLInputElement
  private readonly rivalPanel: HTMLElement
  private readonly computerOnly: readonly HTMLElement[]
  private readonly onlineButtons: HTMLElement
  private readonly findButton: HTMLButtonElement
  private readonly challengeButton: HTMLButtonElement
  private readonly acceptButtons: HTMLElement
  private readonly acceptButton: HTMLButtonElement
  private readonly challengeBanner: HTMLElement
  private mode: GameModeKey = DEFAULT_MODE
  private aiTier: AiTier = DEFAULT_AI_TIER
  private seed: SeedEntry = RANDOM_SEED
  private opponent: Opponent
  private challenge: InviteChallenge | null
  private started = false

  constructor(
    context: ScreenContext,
    private readonly actions: SetupActions,
    challenge: InviteChallenge | null,
  ) {
    const signal = context.signal
    this.challenge = challenge
    this.opponent = challenge === null ? 'computer' : 'online'
    this.overlay = el('div', 'tw-screen tw-setup')
    this.tuning = context.tuning
    applyScreenTheme(this.overlay, context.tuning, context.settings)

    const modes = el('fieldset', 'tw-setup__modes tw-panel')
    modes.append(el('legend', 'tw-heading', 'Game mode'))
    const modeOptions = el('div', 'tw-setup__mode-options')
    modeOptions.setAttribute('role', 'radiogroup')
    modeOptions.setAttribute('aria-label', 'Game mode')
    for (const mode of GAME_MODE_KEYS) modeOptions.append(isModePlayable(mode) ? modeCard(mode, this.selectMode, signal) : comingSoonCard(mode))
    modes.append(modeOptions)

    const opponents = el('fieldset', 'tw-setup__opponents tw-panel')
    opponents.append(el('legend', 'tw-heading', 'Opponent'))
    const opponentOptions = el('div', 'tw-setup__opponent-options')
    opponentOptions.setAttribute('role', 'radiogroup')
    opponentOptions.setAttribute('aria-label', 'Opponent')
    const computerOption = this.opponentOption('computer', 'The computer', 'You pick its nation and how ruthless it is.', signal)
    const onlineOption = this.opponentOption('online', 'Online', 'A rival from your continent, or a friend you challenge by link.', signal)
    opponentOptions.append(computerOption.label, onlineOption.label)
    this.onlineInput = onlineOption.input
    opponents.append(opponentOptions)

    this.you = new CountryPicker('Your nation', 'tw-setup-you', this.refresh, signal)
    this.enemy = new CountryPicker('Enemy nation', 'tw-setup-enemy', this.refresh, signal)
    this.rivalPanel = el('div', 'tw-setup__rival tw-panel')
    this.rivalPanel.append(
      el('span', 'tw-heading', 'Rival nation'),
      el('p', 'tw-dim', 'Your rival brings their own. If they pick yours, one of you is reassigned — the paperwork decides who.'),
    )
    const nations = el('div', 'tw-setup__nations')
    nations.append(this.you.element, el('span', 'tw-setup__versus', 'VS'), this.enemy.element, this.rivalPanel)

    const tiers = el('fieldset', 'tw-setup__tiers tw-panel')
    tiers.append(el('legend', 'tw-heading', 'Enemy AI'))
    const tierOptions = el('div', 'tw-setup__tier-options')
    tierOptions.setAttribute('role', 'radiogroup')
    tierOptions.setAttribute('aria-label', 'Enemy AI difficulty')
    for (const tier of AI_TIERS) {
      const input = el('input', 'tw-setup__tier-radio')
      input.type = 'radio'
      input.name = 'tw-setup-tier'
      input.value = tier
      input.checked = tier === DEFAULT_AI_TIER
      input.addEventListener('change', () => {
        this.aiTier = tier
        this.refresh()
      }, { signal })
      const option = el('label', 'tw-setup__tier')
      const presentation = AI_TIER_PRESENTATION[tier]
      option.append(
        input,
        el('span', 'tw-setup__tier-name', presentation.name),
        el('span', 'tw-setup__tier-blurb tw-dim', presentation.blurb),
        el('span', 'tw-setup__tier-stat tw-dim', `Reaction time ${AI_TIER_DEFS[tier].reactionDelaySeconds} s`),
      )
      tierOptions.append(option)
    }
    tiers.append(tierOptions)

    const seedPanel = el('div', 'tw-setup__seed tw-panel')
    const seedLabel = el('label', 'tw-heading', 'Map seed')
    seedLabel.htmlFor = 'tw-setup-seed'
    this.seedInput = el('input', 'tw-setup__seed-input')
    this.seedInput.id = 'tw-setup-seed'
    this.seedInput.inputMode = 'numeric'
    this.seedInput.autocomplete = 'off'
    this.seedInput.placeholder = 'random'
    this.seedInput.setAttribute('aria-describedby', 'tw-setup-seed-note')
    this.seedInput.addEventListener('input', () => {
      this.seed = parseSeed(this.seedInput.value)
      this.refresh()
    }, { signal })
    this.seedInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') this.start()
    }, { signal })
    const seedNote = el('p', 'tw-setup__seed-note tw-dim', SEED_HELP)
    seedNote.id = 'tw-setup-seed-note'
    seedPanel.append(seedLabel, this.seedInput, seedNote)
    this.seedNote = new TextSlot(seedNote)

    const options = el('div', 'tw-setup__options')
    options.append(tiers, seedPanel)

    const summary = el('p', 'tw-setup__summary')
    this.summary = new TextSlot(summary)
    this.startButton = button('tw-button tw-button--primary tw-setup__start', 'Declare war')
    this.startButton.addEventListener('click', () => this.start(), { signal })
    this.findButton = button('tw-button tw-button--primary tw-setup__start', 'Find a rival')
    this.findButton.addEventListener('click', () => this.goOnline({ kind: 'quick' }), { signal })
    this.challengeButton = button('tw-button tw-setup__start', 'Challenge a friend')
    this.challengeButton.addEventListener('click', () => this.goOnline(newChallenge()), { signal })
    this.onlineButtons = el('div', 'tw-setup__online-actions')
    this.onlineButtons.append(this.challengeButton, this.findButton)
    this.acceptButton = button('tw-button tw-button--primary tw-setup__start', 'Accept challenge')
    this.acceptButton.addEventListener('click', () => {
      if (this.challenge !== null) this.goOnline({ kind: 'accept', code: this.challenge.code })
    }, { signal })
    const decline = button('tw-button tw-setup__start', 'Decline')
    decline.addEventListener('click', () => {
      this.challenge = null
      this.refresh()
    }, { signal })
    this.acceptButtons = el('div', 'tw-setup__online-actions')
    this.acceptButtons.append(decline, this.acceptButton)
    const footer = el('footer', 'tw-setup__footer')
    footer.append(summary, this.startButton, this.onlineButtons, this.acceptButtons)
    this.challengeBanner = el('p', 'tw-setup__challenge', 'You have been challenged to a war. Pick your nation, then accept — the invoice is shared.')

    const header = el('header', 'tw-setup__header')
    header.append(
      el('h1', 'tw-setup__title', 'Tax Globe War'),
      el('p', 'tw-dim', 'Skirmish procurement form 27-B. Name both belligerents; the invoice follows.'),
    )

    this.computerOnly = [this.enemy.element, options]
    const frame = el('main', 'tw-setup__frame')
    frame.append(header, this.challengeBanner, modes, opponents, nations, options, footer)
    this.overlay.append(frame)
    canvasHost(context.renderer).append(this.overlay)
    this.refresh()
  }

  resize(): void {
    // The overlay is laid out by CSS; nothing is sized in code.
  }

  applySettings(settings: VisualSettings): void {
    applyScreenTheme(this.overlay, this.tuning, settings)
  }

  dispose(): void {
    this.overlay.remove()
  }

  private readonly selectMode = (mode: GameModeKey): void => {
    this.mode = mode
    // A mode without an online queue (a preview on the dev server) is a computer war.
    if (!playableOnline(mode)) this.opponent = 'computer'
    this.refresh()
  }

  private opponentOption(opponent: Opponent, name: string, blurb: string, signal: AbortSignal): { readonly label: HTMLLabelElement; readonly input: HTMLInputElement } {
    const input = el('input', 'tw-setup__opponent-radio')
    input.type = 'radio'
    input.name = 'tw-setup-opponent'
    input.value = opponent
    input.checked = opponent === this.opponent
    input.addEventListener('change', () => {
      this.opponent = opponent
      this.refresh()
    }, { signal })
    const label = el('label', 'tw-setup__opponent')
    label.append(input, el('span', 'tw-setup__opponent-name', name), el('span', 'tw-setup__opponent-blurb tw-dim', blurb))
    return { label, input }
  }

  private readonly refresh = (): void => {
    const online = this.opponent === 'online'
    const onlineMode = playableOnline(this.mode)
    this.onlineInput.disabled = !onlineMode
    this.onlineInput.title = onlineMode ? '' : ONLINE_ONLY_DUELS
    this.onlineInput.checked = online
    for (const element of this.computerOnly) element.hidden = online
    this.rivalPanel.hidden = !online
    this.challengeBanner.hidden = !online || this.challenge === null
    this.startButton.hidden = online
    this.onlineButtons.hidden = !online || this.challenge !== null
    this.acceptButtons.hidden = !online || this.challenge === null
    if (online) this.refreshOnline()
    else this.refreshComputer()
  }

  private refreshComputer(): void {
    this.enemy.exclude(this.you.value)
    this.you.exclude(this.enemy.value)
    const you = this.you.value
    const enemy = this.enemy.value
    const seedValid = this.seed.kind !== 'invalid'
    this.seedInput.setAttribute('aria-invalid', seedValid ? 'false' : 'true')
    this.seedNote.set(seedValid ? SEED_HELP : SEED_ERROR)
    this.seedNote.element.classList.toggle('is-error', !seedValid)
    this.startButton.disabled = this.started || you === null || enemy === null || !seedValid
    const modeName = MODE_PRESENTATION[this.mode].name
    if (you === null || enemy === null) {
      this.summary.set(`${modeName} · Select both nations to proceed.`)
      return
    }
    this.summary.set(`${modeName} · ${countryName(you)} vs ${countryName(enemy)} · ${AI_TIER_PRESENTATION[this.aiTier].name} AI`)
  }

  private refreshOnline(): void {
    // Online, only your own nation is yours to pick.
    this.you.exclude(null)
    const you = this.you.value
    const blocked = this.started || you === null
    this.findButton.disabled = blocked
    this.challengeButton.disabled = blocked
    this.acceptButton.disabled = blocked
    const modeName = MODE_PRESENTATION[this.mode].name
    if (you === null) {
      this.summary.set(`${modeName} · online · Select your nation to proceed.`)
      return
    }
    this.summary.set(`${modeName} · online · You lead ${countryName(you)}; your rival brings their own nation.`)
  }

  private goOnline(entry: OnlineEntry): void {
    const you = this.you.value
    if (this.started || you === null || !playableOnline(this.mode)) return
    this.started = true
    this.refresh()
    this.actions.startOnline({ mode: this.mode, country: you, entry })
  }

  private start(): void {
    const you = this.you.value
    const enemy = this.enemy.value
    const entry = this.seed
    if (this.opponent !== 'computer' || this.started || you === null || enemy === null || entry.kind === 'invalid') return
    this.started = true
    this.startButton.disabled = true
    const seed = entry.kind === 'fixed' ? entry.seed : randomSeed()
    const belligerents: Belligerent[] = [{ country: enemy, control: { kind: 'ai', tier: this.aiTier } }]
    belligerents.splice(HUMAN_SEAT, 0, { country: you, control: HUMAN_CONTROL })
    this.actions.startLocal({ mode: this.mode, seed, belligerents })
  }
}
